import React, { createContext, useContext, useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';

const CartContext = createContext();

export function CartProvider({ children }) {
    const [cart, setCart] = useState({ items: [] });
    const [user, setUser] = useState(null);

    // Calculate cart count directly from cart state (derived state)
    // This ensures instant updates without useEffect delays
    const cartCount = cart.items ? cart.items.reduce((sum, item) => sum + item.quantity, 0) : 0;

    // Load user and cart on mount
    useEffect(() => {
        const loadUserAndCart = async () => {
            // Skip auth check    // Race Condition Prevention: Don't check auth on callback pages
            if (window.location.pathname.includes('/auth/google/callback') || window.location.pathname.includes('/oauth/verify')) {
                return;
            }

            try {
                const currentUser = await base44.auth.me();
                // ... rest of logic

                // Fetch farmer profile to check self-purchase
                const farmers = await base44.entities.Farmer.filter({ user_email: currentUser.email });
                if (farmers.length > 0) {
                    currentUser.farmer_id = farmers[0].id;
                }

                setUser(currentUser);
                loadCart(currentUser);
            } catch (e) {
                // Guest user
                loadCart(null);
            }
        };
        loadUserAndCart();
    }, []);

    const loadCart = (currentUser = user) => {
        if (currentUser) {
            const userCartKey = `cart_${currentUser.email}`;
            let userCart = JSON.parse(localStorage.getItem(userCartKey) || '{"items":[]}');
            const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '{"items":[]}');

            // If there are items in the guest cart from before login, merge them into user's cart
            if (guestCart.items && guestCart.items.length > 0) {
                const mergedItems = [...(userCart.items || [])];
                for (const gItem of guestCart.items) {
                    const existingIdx = mergedItems.findIndex(i => i.product_id === gItem.product_id);
                    if (existingIdx >= 0) {
                        mergedItems[existingIdx].quantity += gItem.quantity;
                    } else {
                        mergedItems.push(gItem);
                    }
                }
                userCart = { items: mergedItems };
                localStorage.setItem(userCartKey, JSON.stringify(userCart));
                localStorage.removeItem('guest_cart');
            }
            setCart(userCart);
        } else {
            const savedCart = JSON.parse(localStorage.getItem('guest_cart') || '{"items":[]}');
            setCart(savedCart);
        }
    };

    const saveCart = (newCart) => {
        const cartKey = user ? `cart_${user.email}` : 'guest_cart';
        localStorage.setItem(cartKey, JSON.stringify(newCart));
        setCart(newCart);

        // Dispatch custom event for cart updates
        window.dispatchEvent(new CustomEvent('cartUpdated', { detail: newCart }));
    };

    const addToCart = (product, quantity = 1) => {
        // 1. Prevent Self-Purchase
        if (user?.farmer_id && product.farmer_id === user.farmer_id) {
            throw new Error("You cannot purchase your own products.");
        }

        // 2. Validate Stock
        const existingItem = cart.items.find(item => item.product_id === product.id);
        const currentQuantity = existingItem ? existingItem.quantity : 0;
        const totalRequested = currentQuantity + quantity;

        if (totalRequested > product.quantity_available) {
            throw new Error(`Cannot add ${quantity} more. Only ${product.quantity_available - currentQuantity} available.`);
        }

        let newCart;
        if (existingItem) {
            newCart = {
                ...cart,
                items: cart.items.map(item =>
                    item.product_id === product.id
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                )
            };
        } else {
            newCart = {
                ...cart,
                items: [
                    ...cart.items,
                    {
                        product_id: product.id,
                        product_title: product.title,
                        farmer_id: product.farmer_id,
                        farmer_name: product.farmer_name,
                        quantity: quantity,
                        unit: product.unit,
                        price_per_unit: product.price,
                        image: product.images?.[0],
                        max_quantity: product.quantity_available
                    }
                ]
            };
        }

        saveCart(newCart);
        return newCart;
    };

    const updateQuantity = (productId, newQuantity) => {
        if (newQuantity < 1) return;

        const item = cart.items.find(i => i.product_id === productId);
        if (item && newQuantity > item.max_quantity) {
            // Optionally trigger a toast here if possible, or just don't update
            // Since this is void return, we can't easily throw error to UI unless we change signature.
            // We'll just cap it or return.
            return;
        }

        const newCart = {
            ...cart,
            items: cart.items.map(item =>
                item.product_id === productId
                    ? { ...item, quantity: newQuantity }
                    : item
            )
        };

        saveCart(newCart);
    };

    const removeItem = (productId) => {
        const newCart = {
            ...cart,
            items: cart.items.filter(item => item.product_id !== productId)
        };

        saveCart(newCart);
    };

    const clearCart = () => {
        saveCart({ items: [] });
    };

    const getCartTotal = () => {
        return cart.items.reduce((sum, item) => sum + (item.price_per_unit * item.quantity), 0);
    };

    return (
        <CartContext.Provider
            value={{
                cart,
                cartCount,
                user,
                addToCart,
                updateQuantity,
                removeItem,
                clearCart,
                getCartTotal,
                loadCart
            }}
        >
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
}
